/**
 * SuperAdmin → Módulos Avanzados (1-nov-2026): funciones que no son para todos los clientes.
 * Catálogo: una fila por módulo con su resumen y la tendencia de los últimos 6 meses.
 * Detalle: las empresas que lo tienen y, al lado, la lista para asignarlo a otras (sin modales).
 * Asignar o quitar solo abre o cierra la puerta del módulo; su código no cambia.
 */
import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import clsx from 'clsx';
import { ArrowLeft, ArrowRight, Search } from 'lucide-react';
import SuperadminLayout from '../components/SuperadminLayout';
import { panelApi } from '../utils/api';
import { Encabezado, Boton, Cargando, Aviso, fFecha } from '../components/ui';

const MES = ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Set', 'Oct', 'Nov', 'Dic'];
const nombreMes = (ym) => MES[Number(String(ym).slice(5, 7)) - 1] || ym;

function Estado({ estado }) {
  return estado === 'disponible'
    ? <span className="text-[11px] font-semibold px-2.5 py-0.5 rounded-full border border-emerald-500 text-emerald-700 bg-emerald-50/50">Disponible</span>
    : <span className="text-[11px] font-semibold px-2.5 py-0.5 rounded-full border border-amber-400 text-amber-700 bg-amber-50/50">En construcción</span>;
}

// Barras por mes con su etiqueta; el mes actual resaltado y con su valor
function Tendencia({ uso, titulo }) {
  const vals = uso?.valores || [], meses = uso?.meses || [];
  const w = 360, h = 108, base = 88, top = 14, gap = 12, n = vals.length || 1;
  const bw = (w - gap * (n - 1)) / n, max = Math.max(...vals, 1);
  const vacio = !vals.some(v => v > 0);
  return (
    <div>
      <div className="flex justify-between text-[11px] uppercase tracking-wider text-gray-500 mb-2">
        <span>{titulo} por mes</span>
        <span>{meses.length ? `${nombreMes(meses[0])}–${nombreMes(meses[meses.length - 1])} ${String(meses[meses.length - 1]).slice(0, 4)}` : ''}</span>
      </div>
      <svg viewBox={`0 0 ${w} ${h}`} preserveAspectRatio="none" className="w-full h-[108px] overflow-visible" role="img" aria-label={`${titulo} de los últimos 6 meses`}>
        <line x1="0" y1={base} x2={w} y2={base} stroke="#E5E7EB" strokeWidth="1" vectorEffect="non-scaling-stroke" />
        {vals.map((v, i) => {
          const bh = vacio ? 0 : (v / max) * (base - top), x = i * (bw + gap), ult = i === n - 1;
          return (
            <g key={i}>
              {bh > 0 && <rect x={x} y={base - bh} width={bw} height={bh} rx="3" fill={ult ? '#4652B8' : '#C5CAEE'} />}
              {ult && !vacio && <text x={x + bw / 2} y={base - bh - 4} textAnchor="middle" fontSize="11" fontWeight="600" fill="#1F2937">{v}</text>}
              <text x={x + bw / 2} y={h - 4} textAnchor="middle" fontSize="10.5" fill="#6B7280">{nombreMes(meses[i])}</text>
            </g>
          );
        })}
        {vacio && <text x={w / 2} y={base / 2 + 4} textAnchor="middle" fontSize="12" fill="#9CA3AF">Sin movimiento todavía</text>}
      </svg>
    </div>
  );
}

function Numeros({ m }) {
  const vals = m.uso?.valores || [], a = vals[vals.length - 2] || 0, b = vals[vals.length - 1] || 0;
  const varTxt = a === b ? 'igual' : a ? `${b > a ? '+' : ''}${Math.round((b - a) / a * 100)} %` : `+${b}`;
  const uc = m.ultimoCambio;
  return (
    <div className="grid grid-cols-2 gap-x-5 gap-y-4 content-center h-full">
      <div><p className="text-[11px] uppercase tracking-wider text-gray-500 mb-0.5">Empresas</p><p className="text-2xl font-semibold tabular-nums">{m.empresas}</p></div>
      <div><p className="text-[11px] uppercase tracking-wider text-gray-500 mb-0.5">Cuentas</p><p className="text-2xl font-semibold tabular-nums">{m.cuentas}</p></div>
      <div>
        <p className="text-[11px] uppercase tracking-wider text-gray-500 mb-0.5">{m.usoNombre} · {nombreMes(m.uso?.meses?.slice(-1)[0])}</p>
        <p className="text-2xl font-semibold tabular-nums">{b}<span className={clsx('text-xs font-medium ml-1.5', b > a ? 'text-emerald-600' : 'text-gray-400')}>{varTxt}</span></p>
      </div>
      <div>
        <p className="text-[11px] uppercase tracking-wider text-gray-500 mb-0.5">Último cambio</p>
        <p className="text-[13px] text-gray-800 leading-snug">{uc ? `${uc.accion === 'asignar' ? 'Asignado a' : 'Quitado a'} ${uc.empresa || 'una empresa'} · ${fFecha(uc.fecha)}` : 'Sin cambios todavía'}</p>
      </div>
    </div>
  );
}

const Etiquetas = ({ items }) => (
  <div className="flex flex-wrap gap-1.5">{items.map(t => <span key={t} className="text-xs border border-gray-200 rounded-full px-2.5 py-0.5 text-gray-600 whitespace-nowrap">{t}</span>)}</div>
);

export function ModulosAvanzados() {
  const [modulos, setModulos] = useState(null);
  const [aviso, setAviso] = useState(null);
  const navigate = useNavigate();
  useEffect(() => {
    panelApi.modulosAvanzados().then(r => setModulos(r.data.data))
      .catch(e => { setModulos([]); setAviso({ tipo: 'error', texto: e.response?.data?.error || 'No se pudieron leer los módulos' }); });
  }, []);
  return (
    <SuperadminLayout>
      <Encabezado titulo="Módulos Avanzados" subtitulo="Funciones para los clientes que de verdad las necesitan. Vienen apagadas y se asignan por empresa desde cada módulo." />
      <Aviso aviso={aviso} onClose={() => setAviso(null)} />
      {!modulos ? <Cargando /> : (
        <div className="flex flex-col gap-3.5">
          {modulos.map(m => (
            <div key={m.id} role="button" tabIndex={0} onClick={() => navigate(`/superadmin/modulos-avanzados/${m.id}`)}
              onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); navigate(`/superadmin/modulos-avanzados/${m.id}`); } }}
              className="group bg-white border border-gray-200 rounded-2xl grid grid-cols-1 xl:grid-cols-[minmax(0,1.5fr)_minmax(0,1fr)_minmax(0,1.3fr)_auto] cursor-pointer hover:border-[#4652B8] focus:outline-none focus-visible:border-[#4652B8] transition-colors">
              <div className="p-5 min-w-0">
                <div className="flex items-center gap-2.5 flex-wrap"><span className="text-[17px] font-semibold text-gray-900">{m.nombre}</span><Estado estado={m.estado} /></div>
                <p className="text-xs text-gray-500 font-mono mt-1 mb-2.5">{m.area}</p>
                <p className="text-sm text-gray-600 leading-relaxed mb-3">{m.descripcion}</p>
                <Etiquetas items={m.incluye} />
              </div>
              <div className="p-5 min-w-0 border-t xl:border-t-0 xl:border-l border-gray-100"><Numeros m={m} /></div>
              <div className="p-5 min-w-0 border-t xl:border-t-0 xl:border-l border-gray-100"><Tendencia uso={m.uso} titulo={m.usoNombre} /></div>
              <div className="p-5 flex items-center justify-start xl:justify-center border-t xl:border-t-0 xl:border-l border-gray-100">
                <span className="inline-flex items-center gap-1.5 border-[1.5px] border-[#4652B8] text-[#3B45A6] rounded-[10px] px-3.5 py-2 text-sm font-semibold whitespace-nowrap group-hover:bg-[#ECEEFA]">Administrar <ArrowRight className="h-4 w-4" /></span>
              </div>
            </div>
          ))}
        </div>
      )}
    </SuperadminLayout>
  );
}

export function ModuloAvanzadoDetalle() {
  const { id } = useParams();
  const [d, setD] = useState(null);
  const [aviso, setAviso] = useState(null);
  const [q, setQ] = useState('');
  const [sel, setSel] = useState(new Set());
  const [trabajando, setTrabajando] = useState(false);
  const cargar = useCallback(() => panelApi.moduloAvanzado(id).then(r => setD(r.data.data))
    .catch(e => setAviso({ tipo: 'error', texto: e.response?.data?.error || 'No se pudo leer el módulo' })), [id]);
  useEffect(() => { cargar(); }, [cargar]);

  const clave = (e) => `${e.tenantId}|${e.emisorId}`;
  const alternar = (e) => setSel(s => { const n = new Set(s); n.has(clave(e)) ? n.delete(clave(e)) : n.add(clave(e)); return n; });
  const asignar = async () => {
    setTrabajando(true);
    try {
      const empresas = d.disponibles.filter(e => sel.has(clave(e))).map(e => ({ tenantId: e.tenantId, emisorId: e.emisorId }));
      const r = await panelApi.asignarModulo(id, empresas);
      setAviso({ tipo: 'ok', texto: `${r.data.data.asignadas} empresa${r.data.data.asignadas === 1 ? '' : 's'} con el módulo ${d.modulo.nombre}` });
      setSel(new Set()); await cargar();
    } catch (e) { setAviso({ tipo: 'error', texto: e.response?.data?.error || 'No se pudo asignar' }); }
    finally { setTrabajando(false); }
  };
  const quitar = async (e) => {
    setTrabajando(true);
    try { await panelApi.quitarModulo(id, e.tenantId, e.emisorId); setAviso({ tipo: 'ok', texto: `${e.nombre} ya no tiene el módulo ${d.modulo.nombre}` }); await cargar(); }
    catch (err) { setAviso({ tipo: 'error', texto: err.response?.data?.error || 'No se pudo quitar' }); }
    finally { setTrabajando(false); }
  };

  const libres = (d?.disponibles || []).filter(e => !q || `${e.nombre} ${e.cuenta} ${e.cedula}`.toLowerCase().includes(q.trim().toLowerCase()));
  const porCuenta = libres.reduce((g, e) => { (g[e.cuenta] ||= []).push(e); return g; }, {});
  const enConstruccion = d?.modulo.estado !== 'disponible';

  return (
    <SuperadminLayout>
      <Link to="/superadmin/modulos-avanzados" className="inline-flex items-center gap-1 text-sm text-gray-500 hover:text-[#4652B8] mb-3"><ArrowLeft className="h-4 w-4" /> Módulos Avanzados</Link>
      <Aviso aviso={aviso} onClose={() => setAviso(null)} />
      {!d ? <Cargando /> : (
        <div className="flex flex-col gap-4">
          <div className="bg-white border border-gray-200 rounded-2xl grid grid-cols-1 xl:grid-cols-[minmax(0,1.35fr)_minmax(0,1fr)_minmax(0,1.25fr)]">
            <div className="p-5 min-w-0">
              <h1 className="text-[22px] font-extrabold text-gray-900 flex items-center gap-2.5 flex-wrap">{d.modulo.nombre}<Estado estado={d.modulo.estado} /></h1>
              <p className="text-sm text-gray-600 mt-1.5 mb-3 leading-relaxed">{d.modulo.descripcion}</p>
              <Etiquetas items={d.modulo.incluye} />
            </div>
            <div className="p-5 min-w-0 border-t xl:border-t-0 xl:border-l border-gray-100"><Numeros m={d.modulo} /></div>
            <div className="p-5 min-w-0 border-t xl:border-t-0 xl:border-l border-gray-100"><Tendencia uso={d.modulo.uso} titulo={d.modulo.usoNombre} /></div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1.3fr)_minmax(0,1fr)] gap-4">
            <section className="bg-white border border-gray-200 rounded-2xl flex flex-col h-[460px] min-w-0">
              <div className="px-5 py-3.5 border-b border-gray-200 text-xs font-semibold uppercase tracking-wider text-gray-500">
                Empresas con el módulo <span className="text-gray-900 ml-1 tabular-nums">{d.asignadas.length}</span>
              </div>
              <div className="flex-1 min-h-0 overflow-auto">
                {d.asignadas.length ? (
                  <table className="w-full text-sm">
                    <thead className="sticky top-0 bg-white"><tr className="text-left text-[11px] uppercase tracking-wider text-gray-500 border-b border-gray-200">
                      <th className="px-5 py-2.5 font-semibold">Empresa</th><th className="px-5 py-2.5 font-semibold">Cuenta</th><th className="px-5 py-2.5 font-semibold">Cédula</th><th className="px-5 py-2.5 font-semibold">Asignada</th><th></th>
                    </tr></thead>
                    <tbody>
                      {d.asignadas.map(e => (
                        <tr key={clave(e)} className="border-b border-gray-100">
                          <td className="px-5 py-2.5 whitespace-nowrap">{e.nombre}</td>
                          <td className="px-5 py-2.5 font-mono text-xs text-gray-500">{e.cuenta}</td>
                          <td className="px-5 py-2.5 font-mono text-xs text-gray-500 tabular-nums">{e.cedula}</td>
                          <td className="px-5 py-2.5 font-mono text-xs text-gray-500" title={e.asignadoPor ? `Por ${e.asignadoPor}` : undefined}>{fFecha(e.desde)}</td>
                          <td className="px-5 py-2.5 text-right"><button disabled={trabajando} onClick={() => quitar(e)} className="text-sm text-red-600 border border-transparent hover:border-red-400 rounded-lg px-2.5 py-1 disabled:opacity-40">Quitar</button></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                ) : (
                  <div className="h-full flex flex-col items-center justify-center gap-1 text-center px-6">
                    <p className="font-semibold text-gray-800">Ninguna empresa tiene este módulo</p>
                    <p className="text-sm text-gray-500">{enConstruccion ? 'Se podrá asignar cuando el módulo esté disponible.' : 'Elija empresas en el panel de la derecha y asígnelas.'}</p>
                  </div>
                )}
              </div>
            </section>

            <section className="bg-white border border-gray-200 rounded-2xl flex flex-col h-[460px] min-w-0">
              <div className="px-5 py-3.5 border-b border-gray-200 text-xs font-semibold uppercase tracking-wider text-gray-500">Asignar a empresas</div>
              <div className="px-5 py-3 border-b border-gray-200 relative">
                <Search className="h-4 w-4 text-gray-400 absolute left-8 top-1/2 -translate-y-1/2" />
                <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar empresa, cuenta o cédula"
                  className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:border-[#4652B8]" />
              </div>
              <div className="flex-1 min-h-0 overflow-auto py-1">
                {Object.keys(porCuenta).length ? Object.entries(porCuenta).map(([cuenta, es]) => (
                  <div key={cuenta}>
                    <p className="px-5 pt-2.5 pb-1 text-[11px] font-semibold uppercase tracking-wider text-gray-400 font-mono">{cuenta}</p>
                    {es.map(e => (
                      <label key={clave(e)} className={clsx('grid grid-cols-[auto_minmax(0,1fr)_auto] items-center gap-3 px-5 py-2', enConstruccion ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer hover:bg-[#F3F4FC]')}>
                        <input type="checkbox" disabled={enConstruccion} checked={sel.has(clave(e))} onChange={() => alternar(e)} className="accent-[#4652B8] h-4 w-4" />
                        <span className="text-sm truncate">{e.nombre}</span>
                        <span className="font-mono text-xs text-gray-500 tabular-nums">{e.cedula}</span>
                      </label>
                    ))}
                  </div>
                )) : <p className="h-full flex items-center justify-center text-sm text-gray-500">{q ? 'Ninguna empresa coincide' : 'Todas las empresas tienen el módulo'}</p>}
              </div>
              <div className="px-5 py-3 border-t border-gray-200 flex items-center justify-between gap-3">
                <span className="text-xs text-gray-500">{enConstruccion ? 'Módulo en construcción' : sel.size ? `${sel.size} seleccionada${sel.size === 1 ? '' : 's'}` : 'Ninguna seleccionada'}</span>
                <Boton variante="primario" disabled={!sel.size || enConstruccion} cargando={trabajando} onClick={asignar}>Asignar</Boton>
              </div>
            </section>
          </div>
        </div>
      )}
    </SuperadminLayout>
  );
}
